import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * Simple full-screen lightbox for images/videos. Rendered via portal-like fixed
 * overlay. Closes on Escape or backdrop click. Preserves media aspect ratio.
 */
export default function Lightbox({ media, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!media) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6"
      style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="lightbox-overlay"
      role="dialog"
      aria-label="Media preview"
    >
      <button
        onClick={onClose}
        className="press absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center"
        style={{ border: '1px solid rgba(245,165,36,0.5)', color: 'var(--amber)', borderRadius: 4, background: 'rgba(10,7,4,0.6)' }}
        aria-label="Close preview"
        data-testid="lightbox-close"
      >
        <X size={16} strokeWidth={2} />
      </button>
      <div
        className="relative max-w-[92vw] max-h-[88vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        style={{ border: '1px solid rgba(245,165,36,0.45)', borderRadius: 4, background: '#050301', boxShadow: '0 30px 80px -20px rgba(245,165,36,0.35)' }}
      >
        {media.type === 'video' ? (
          <video
            src={media.src}
            controls
            autoPlay
            loop
            playsInline
            className="block max-w-[92vw] max-h-[88vh] w-auto h-auto"
            data-testid="lightbox-video"
          />
        ) : (
          <img
            src={media.src}
            alt={media.caption || 'expanded media'}
            className="block max-w-[92vw] max-h-[88vh] w-auto h-auto"
            data-testid="lightbox-image"
          />
        )}
        {media.caption && (
          <div
            className="absolute left-0 right-0 bottom-0 px-3 py-2 text-[12px]"
            style={{ color: 'var(--amber)', background: 'linear-gradient(180deg, transparent, rgba(5,5,5,0.9))', letterSpacing: '0.04em' }}
          >
            {media.caption}
          </div>
        )}
      </div>
    </div>
  );
}
